import { ReactNode, useState, useEffect } from 'react';
import { Box, CircularProgress, Snackbar, Alert } from '@mui/material';
import { useAuth } from '../../contexts/identityaccess/application/hooks/AuthContext';
import { useNotification } from '../../shared/contexts/useNotification';
import { SideMenu } from './components/SideMenu';
import { AppNavbar } from './components/AppNavbar';
import { MobileDrawer } from './components/MobileDrawer';
import { Header } from './components/Header';
import { designTokens } from '../theme/design-tokens';

export interface DashboardLayoutProps {
  /**
   * Page content to render
   */
  children: ReactNode;
}

/**
 * Dashboard Layout Component
 *
 * Main layout wrapper for the admin panel.
 * Provides responsive navigation with permanent sidebar (desktop)
 * or temporary drawer (mobile).
 *
 * Layout structure:
 * - Desktop (≥900px): Permanent sidebar + Header + main content
 * - Mobile (<900px): App bar + temporary drawer + main content
 *
 * The layout automatically retrieves user data from AuthContext
 * and handles logout functionality.
 *
 * @example
 * ```tsx
 * <DashboardLayout>
 *   <DashboardPage />
 * </DashboardLayout>
 * ```
 */
export function DashboardLayout({ children }: DashboardLayoutProps) {
  const { admin, loadAdmin, logout } = useAuth();
  const { notification, hideNotification } = useNotification();
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const [isLoadingAdmin, setIsLoadingAdmin] = useState(!admin);

  /**
   * Lazy-load admin data when layout mounts
   * This only runs if admin data hasn't been loaded yet
   */
  useEffect(() => {
    const fetchAdmin = async () => {
      if (!admin) {
        setIsLoadingAdmin(true);
        try {
          await loadAdmin();
        } catch (error) {
          console.error('Failed to load admin data:', error);
        } finally {
          setIsLoadingAdmin(false);
        }
      }
    };
    fetchAdmin();
  }, [admin, loadAdmin]);

  /**
   * Handle mobile drawer toggle
   */
  const handleDrawerToggle = () => {
    setMobileDrawerOpen((prev) => !prev);
  };

  /**
   * Handle logout action
   */
  const handleLogout = () => {
    logout();
  };

  // Show loading spinner while fetching admin data
  if (isLoadingAdmin || !admin) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {/* Mobile App Bar */}
      <AppNavbar onMenuClick={handleDrawerToggle} />

      {/* Desktop Sidebar */}
      <SideMenu onLogout={handleLogout} />

      {/* Mobile Drawer */}
      <MobileDrawer
        open={mobileDrawerOpen}
        onClose={handleDrawerToggle}
        name={admin.name}
        email={admin.email}
        onLogout={handleLogout}
      />

      {/* Main Content Area */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          backgroundColor: designTokens.colors.background,
          display: 'flex',
          flexDirection: 'column',
          height: '100vh',
          overflow: 'hidden',
        }}
      >
        <Header name={admin.name} email={admin.email} onLogout={handleLogout} />

        <Box sx={{ flex: 1, overflow: 'auto', pb: 5 }}>{children}</Box>
      </Box>

      {/* Global Notifications */}
      <Snackbar
        open={notification.open}
        autoHideDuration={notification.autoHideDuration}
        onClose={hideNotification}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={hideNotification}
          severity={notification.severity}
          variant="filled"
          sx={{ width: '100%', boxShadow: 3 }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
